import { initContract } from '@ts-rest/core'
import z from 'zod'
import { createSortQuerySchema, ErrorResponseSchema, PaginatedResultsQuerySchema } from '../../../shared/types'
import { PaginatedListOf } from '../schemas'
import {
  AnimalCleanupMigrationInputSchema,
  AnimalCleanupMigrationOutputSchema,
  AnimalMigrationInputSchema,
  AnimalMigrationOutputSchema,
  AnimalVerificationOutputSchema,
  CreateDogBodySchema,
  DeactivateDogBodySchema,
  DeleteMigratedDogHistoryDataRequestSchema,
  DeleteMigratedDogHistoryDataResponseSchema,
  DogBreedDtoSchema,
  DogDeactivationReasonDtoSchema,
  DogDtoSchema,
  DogFoodAllergenDtoSchema,
  DogFoodTypeDtoSchema,
  DogHealthIssueDtoSchema,
  GetDogRecommendationBodySchema,
  GetDogRecommendationResponseSchema,
  GetPuppiesForGraduationQuerySchema,
  GetPuppiesForGraduationResponseSchema,
  MigrateDogHistoryDataRequestSchema,
  MigrateDogHistoryOutputSchema,
  UpdateDogBodySchema,
  VerifyMigratedDogHistoryDataRequestSchema,
  VerifyMigratedDogHistoryDataResponseSchema,
} from './dogs.schemas'
import { PuppyRecommendationResponseSchema } from './puppy-recommendation.schemas'

export const DOG_SORT_COLUMNS = ['id', 'name', 'deactivatedAt', 'createdAt'] as const

const c = initContract()

const options = {
  commonResponses: {
    400: ErrorResponseSchema,
    404: ErrorResponseSchema,
    500: ErrorResponseSchema,
    501: ErrorResponseSchema,
  },
  pathPrefix: '/animals/dogs',
}

export const animalDogsContract = c.router({
  getDogBreeds: {
    method: 'GET',
    path: '/breeds',
    description: 'Get all dog breeds',
    responses: {
      200: DogBreedDtoSchema.array(),
    },
  },
  getDogBreedsBySlug: {
    method: 'GET',
    path: '/breeds/by-slug/:slug',
    description: 'Get a dog breed by slug',
    pathParams: z.object({
      slug: z.string(),
    }),
    responses: {
      200: DogBreedDtoSchema,
    },
  },
  getDogHealthIssues: {
    method: 'GET',
    path: '/health-issues',
    description: 'Get all dog health issues',
    responses: {
      200: DogHealthIssueDtoSchema.array(),
    },
  },
  getDogHealthIssuesBySlug: {
    method: 'GET',
    path: '/health-issues/by-slug/:slug',
    description: 'Get a dog health issue by slug',
    pathParams: z.object({
      slug: z.string(),
    }),
    responses: {
      200: DogHealthIssueDtoSchema,
    },
  },
  getDogFoodTypes: {
    method: 'GET',
    path: '/food-types',
    description: 'Get all dog food types',
    responses: {
      200: DogFoodTypeDtoSchema.array(),
    },
  },
  getDogFoodTypesBySlug: {
    method: 'GET',
    path: '/food-types/by-slug/:slug',
    description: 'Get a dog food type by slug',
    pathParams: z.object({
      slug: z.string(),
    }),
    responses: {
      200: DogFoodTypeDtoSchema,
    },
  },
  getDogFoodAllergens: {
    method: 'GET',
    path: '/food-allergens',
    description: 'Get all dog food allergens',
    responses: {
      200: DogFoodAllergenDtoSchema.array(),
    },
  },
  getDogFoodAllergensBySlug: {
    method: 'GET',
    path: '/food-allergens/by-slug/:slug',
    description: 'Get a dog food allergen by slug',
    pathParams: z.object({
      slug: z.string(),
    }),
    responses: {
      200: DogFoodAllergenDtoSchema,
    },
  },
  getDogRecommendation: {
    method: 'POST',
    path: '/recommendation/generate',
    description: 'Get dog recommendation based on the provided information',
    body: GetDogRecommendationBodySchema,
    responses: {
      200: GetDogRecommendationResponseSchema,
    },
  },
  getDeactivationReasons: {
    method: 'GET',
    path: '/deactivation-reasons',
    description: 'Get all dog deactivation reasons',
    responses: {
      200: DogDeactivationReasonDtoSchema.array(),
    },
  },
  deactivateDog: {
    method: 'POST',
    path: '/:id/deactivate',
    description: 'Deactivate a dog by its ID',
    pathParams: z.object({
      id: DogDtoSchema.shape.id,
    }),
    body: DeactivateDogBodySchema,
    responses: {
      200: DogDtoSchema,
    },
  },
  reactivateDog: {
    method: 'POST',
    path: '/:id/reactivate',
    description: 'Reactivate a dog by its ID',
    pathParams: z.object({
      id: DogDtoSchema.shape.id,
    }),
    body: null,
    responses: {
      200: DogDtoSchema,
    },
  },
  graduatePuppy: {
    method: 'POST',
    path: '/:id/graduate',
    description: 'Graduate a puppy to an adult dog',
    pathParams: z.object({
      id: DogDtoSchema.shape.id,
    }),
    body: null,
    responses: {
      200: DogDtoSchema,
    },
  },
  migrateHistoricalData: {
    method: 'POST',
    path: '/migration/history',
    body: MigrateDogHistoryDataRequestSchema,
    responses: {
      200: MigrateDogHistoryOutputSchema,
    },
  },
  revertMigrateHistoricalData: {
    method: 'DELETE',
    path: '/migration/history',
    body: DeleteMigratedDogHistoryDataRequestSchema,
    responses: {
      200: DeleteMigratedDogHistoryDataResponseSchema,
    },
  },
  verifyMigratedHistoricalData: {
    method: 'POST',
    path: '/migration/history/verify',
    body: VerifyMigratedDogHistoryDataRequestSchema,
    responses: {
      200: VerifyMigratedDogHistoryDataResponseSchema,
    },
  },
  migrate: {
    method: 'POST',
    path: '/migrations',
    body: AnimalMigrationInputSchema,
    responses: {
      200: AnimalMigrationOutputSchema,
    },
  },
  verifyMigration: {
    method: 'POST',
    path: '/migrations/verify',
    body: AnimalMigrationInputSchema,
    responses: {
      200: AnimalVerificationOutputSchema,
    },
  },
  cleanupMigration: {
    method: 'DELETE',
    path: '/migrations/:userId',
    pathParams: AnimalCleanupMigrationInputSchema,
    responses: {
      200: AnimalCleanupMigrationOutputSchema,
    },
  },
  getPuppiesForGraduation: {
    method: 'GET',
    path: '/puppies',
    description: 'Get puppies (dogs with lifestage PUPPY) for auto graduation using cursor-based pagination by animal id',
    query: GetPuppiesForGraduationQuerySchema,
    responses: {
      200: GetPuppiesForGraduationResponseSchema,
    },
  },
  getDog: {
    method: 'GET',
    path: '/:id',
    description: 'Get a dog by ID',
    pathParams: z.object({
      id: z.string().uuid(),
    }),
    responses: {
      200: DogDtoSchema,
    },
  },
  getPuppyRecommendation: {
    method: 'GET',
    path: '/:id/puppy-recommendation',
    description: 'Get the puppy growth and feeding recommendation for a dog (V2 algorithm).',
    pathParams: z.object({
      id: z.string().uuid(),
    }),
    responses: {
      200: PuppyRecommendationResponseSchema,
    },
  },
  updateDog: {
    method: 'PATCH',
    path: '/:id',
    description: 'Update a dog',
    pathParams: z.object({
      id: z.string().uuid(),
    }),
    body: UpdateDogBodySchema,
    responses: {
      200: DogDtoSchema,
    },
  },
  createDog: {
    method: 'POST',
    path: '',
    description: 'Create a new dog',
    body: CreateDogBodySchema,
    responses: {
      201: DogDtoSchema,
    },
  },
  getDogs: {
    method: 'GET',
    path: '',
    description: 'Get dogs by user ID',
    query: PaginatedResultsQuerySchema.extend({
      userId: z.string().uuid(),
      sort: createSortQuerySchema(DOG_SORT_COLUMNS),
    }),
    responses: {
      200: PaginatedListOf(DogDtoSchema),
    },
  },
}, {
  ...options,
})
